import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { cn } from "../lib/utils";
import {
  ArrowLeft,
  DollarSign,
  Calendar,
  User,
  Clock,
  CheckCircle2,
  AlertCircle,
  History,
  Edit3,
  RefreshCw,
  Building2,
  FileText,
  Loader2,
  CreditCard,
  UserCircle,
  Banknote,
  Save,
} from "lucide-react";
import { toast } from "sonner";

const ArtistPaymentDetailPage = () => {
  const { expenseId } = useParams();
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();
  
  const [expense, setExpense] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit mode
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Status change dialog
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusReason, setStatusReason] = useState("");
  const [paidDate, setPaidDate] = useState("");
  const [changingStatus, setChangingStatus] = useState(false);
  
  // Date edit dialog
  const [dateDialogOpen, setDateDialogOpen] = useState(false);
  const [dateField, setDateField] = useState("");
  const [newDate, setNewDate] = useState("");
  const [dateReason, setDateReason] = useState("");
  const [changingDate, setChangingDate] = useState(false);

  useEffect(() => {
    fetchData();
  }, [expenseId]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [expenseRes, artistsRes] = await Promise.all([
        api.get(`/other-expenses/${expenseId}`),
        api.get("/users?role=artist")
      ]);
      setExpense(expenseRes.data);
      setArtists(artistsRes.data || []);
      setEditData({
        payee_name: expenseRes.data.payee_name || "",
        amount: expenseRes.data.amount || 0,
        payment_details: expenseRes.data.payment_details || "",
        project_name: expenseRes.data.project_name || "",
        notes: expenseRes.data.notes || "",
        artist_id: expenseRes.data.artist_id || "",
      });
    } catch (error) {
      toast.error("Failed to load payment details");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async () => {
    setChangingStatus(true);
    try {
      await api.put(`/other-expenses/${expenseId}/change-status`, {
        new_status: newStatus,
        reason: statusReason,
        paid_date: newStatus === "paid" ? paidDate : null
      });
      toast.success("Status changed successfully");
      setStatusDialogOpen(false);
      setStatusReason("");
      setPaidDate("");
      fetchData();
    } catch (error) {
      toast.error("Failed to change status");
    } finally {
      setChangingStatus(false);
    }
  };

  const handleDateChange = async () => {
    if (!newDate) {
      toast.error("Please select a date");
      return;
    }
    
    setChangingDate(true);
    try {
      await api.put(`/other-expenses/${expenseId}/update-field`, {
        field: dateField,
        value: newDate,
        reason: dateReason || "Date correction"
      });
      toast.success("Date updated successfully");
      setDateDialogOpen(false);
      setDateReason("");
      fetchData();
    } catch (error) {
      toast.error("Failed to update date");
    } finally {
      setChangingDate(false);
    }
  };

  const handleSaveField = async (field, value) => {
    try {
      await api.put(`/other-expenses/${expenseId}/update-field`, {
        field,
        value: String(value),
        reason: "Manual edit"
      });
      toast.success(`${field} updated`);
      fetchData();
    } catch (error) {
      toast.error(`Failed to update ${field}`);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      // Update each changed field
      const fields = ["payee_name", "amount", "payment_details", "project_name", "notes", "artist_id"];
      for (const field of fields) {
        if (editData[field] !== expense[field]) {
          await api.put(`/other-expenses/${expenseId}/update-field`, {
            field,
            value: String(editData[field] || ""),
            reason: "Manual edit"
          });
        }
      }
      toast.success("Payment updated successfully");
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Paid</Badge>;
      case "approved":
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30">Approved</Badge>;
      case "cancelled":
        return <Badge className="bg-zinc-500/20 text-zinc-400 border-zinc-500/30">Cancelled</Badge>;
      default:
        return <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">Pending</Badge>;
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString();
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!expense) {
    return (
      <div className="text-center py-8 text-zinc-500">
        <AlertCircle className="h-12 w-12 mx-auto mb-2" />
        <p>Payment not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Go Back
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="artist-payment-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/payments")}
            className="text-zinc-400 hover:text-zinc-50 h-8 w-8"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-zinc-50">{expense.payee_name}</h1>
            <p className="text-xs text-zinc-500">
              {expense.expense_type === "artist_payment" ? "Artist Payment" : expense.expense_type} • {formatDateShort(expense.date)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {getStatusBadge(expense.status)}
          {!isEditing ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsEditing(true)}
              className="border-zinc-700 text-zinc-400 hover:text-zinc-50"
            >
              <Edit3 className="h-3.5 w-3.5 mr-1" />
              Edit
            </Button>
          ) : (
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(false);
                  setEditData({
                    payee_name: expense.payee_name || "",
                    amount: expense.amount || 0,
                    payment_details: expense.payment_details || "",
                    project_name: expense.project_name || "",
                    notes: expense.notes || "",
                    artist_id: expense.artist_id || "",
                  });
                }}
                className="border-zinc-700"
              >
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={handleSaveAll}
                disabled={saving}
                className="bg-orange-500 hover:bg-orange-600"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                Save
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Main Info */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <DollarSign className="h-4 w-4 text-green-400" />
              Payment Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Amount */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase">Amount</Label>
              {isEditing ? (
                <Input
                  type="number"
                  value={editData.amount}
                  onChange={(e) => setEditData({ ...editData, amount: parseFloat(e.target.value) || 0 })}
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1"
                />
              ) : (
                <p className="text-lg font-bold text-green-400">
                  {formatAmount(expense.amount, expense.currency)}
                </p>
              )}
            </div>

            {/* Payee Name */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase">Payee Name</Label>
              {isEditing ? (
                <Input
                  value={editData.payee_name}
                  onChange={(e) => setEditData({ ...editData, payee_name: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1"
                />
              ) : (
                <p className="text-sm text-zinc-50 mt-1">{expense.payee_name}</p>
              )}
            </div>

            {/* Artist Link */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                <UserCircle className="h-3 w-3" />
                Linked Artist
              </Label>
              {isEditing ? (
                <Select
                  value={editData.artist_id || "none"}
                  onValueChange={(v) => setEditData({ ...editData, artist_id: v === "none" ? "" : v })}
                >
                  <SelectTrigger className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1">
                    <SelectValue placeholder="Select artist..." />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none" className="text-zinc-400">No artist linked</SelectItem>
                    {artists.map((artist) => (
                      <SelectItem key={artist.id} value={artist.id} className="text-zinc-50">
                        {artist.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : expense.artist_id ? (
                <p className="text-sm text-purple-400 mt-1">
                  {artists.find(a => a.id === expense.artist_id)?.name || expense.artist_id}
                </p>
              ) : (
                <p className="text-sm text-zinc-500 mt-1">Not linked</p>
              )}
            </div>

            {/* Payment Details */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                <CreditCard className="h-3 w-3" />
                Payment Details (Card/IBAN)
              </Label>
              {isEditing ? (
                <Input
                  value={editData.payment_details}
                  onChange={(e) => setEditData({ ...editData, payment_details: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1 font-mono text-xs"
                  placeholder="Card number, IBAN, crypto wallet..."
                />
              ) : (
                <p className="text-xs text-zinc-300 mt-1 font-mono break-all">
                  {expense.payment_details || "—"}
                </p>
              )}
            </div>

            {/* Project */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                <Building2 className="h-3 w-3" />
                Project
              </Label>
              {isEditing ? (
                <Input
                  value={editData.project_name}
                  onChange={(e) => setEditData({ ...editData, project_name: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1"
                />
              ) : (
                <p className="text-sm text-zinc-50 mt-1">{expense.project_name || "—"}</p>
              )}
            </div>

            {/* Notes */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <Label className="text-[10px] text-zinc-500 uppercase flex items-center gap-1">
                <FileText className="h-3 w-3" />
                Notes
              </Label>
              {isEditing ? (
                <Textarea
                  value={editData.notes}
                  onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                  className="bg-zinc-900 border-zinc-700 text-zinc-50 mt-1 min-h-[60px]"
                />
              ) : (
                <p className="text-sm text-zinc-300 mt-1">{expense.notes || "—"}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Status & Dates */}
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardHeader className="pb-2">
            <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
              <Clock className="h-4 w-4 text-purple-400" />
              Status & Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Current Status */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Current Status</p>
                  <div className="mt-1">{getStatusBadge(expense.status)}</div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setNewStatus(expense.status);
                    setPaidDate(expense.paid_date || new Date().toISOString().slice(0, 10));
                    setStatusDialogOpen(true);
                  }}
                  className="border-zinc-700 text-zinc-400 hover:text-zinc-50 h-8"
                >
                  <RefreshCw className="h-3.5 w-3.5 mr-1" />
                  Change
                </Button>
              </div>
            </div>

            {/* Creation Date */}
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-zinc-500 uppercase">Creation Date</p>
                  <p className="text-sm text-zinc-50 mt-1">{formatDateShort(expense.date)}</p>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setDateField("date");
                    setNewDate(expense.date || "");
                    setDateDialogOpen(true);
                  }}
                  className="text-zinc-400 hover:text-zinc-50 h-8"
                >
                  <Edit3 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>

            {/* Approved Date */}
            {(expense.status === "approved" || expense.status === "paid") && (
              <div className="p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-blue-400 uppercase">Approved Date</p>
                    <p className="text-sm text-blue-300 mt-1">{formatDateShort(expense.approved_date)}</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDateField("approved_date");
                      setNewDate(expense.approved_date || "");
                      setDateDialogOpen(true);
                    }}
                    className="text-blue-400 hover:bg-blue-500/10 h-8"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Paid Date */}
            {expense.status === "paid" && (
              <div className="p-3 bg-green-900/20 border border-green-800/30 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[10px] text-green-400 uppercase">Payment Date</p>
                    <p className="text-sm text-green-300 font-medium mt-1">{formatDateShort(expense.paid_date)}</p>
                    <p className="text-[10px] text-green-600 mt-0.5">Payment recorded in this month's reports</p>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => {
                      setDateField("paid_date");
                      setNewDate(expense.paid_date || "");
                      setDateDialogOpen(true);
                    }}
                    className="text-green-400 hover:bg-green-500/10 h-8"
                  >
                    <Edit3 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Artist Payment Details from Employee Record */}
            {expense.expense_type === "artist_payment" && (expense.artist_iban || expense.artist_card_number) && (
              <div className="p-3 bg-purple-900/20 border border-purple-800/30 rounded-lg">
                <p className="text-[10px] text-purple-400 uppercase mb-2">Artist Banking Details (from profile)</p>
                {expense.artist_recipient_name && (
                  <p className="text-xs text-zinc-400">Recipient: <span className="text-zinc-50">{expense.artist_recipient_name}</span></p>
                )}
                {expense.artist_iban && (
                  <p className="text-xs text-zinc-400">IBAN: <span className="text-zinc-50 font-mono">{expense.artist_iban}</span></p>
                )}
                {expense.artist_card_number && (
                  <p className="text-xs text-zinc-400">Card: <span className="text-zinc-50 font-mono">{expense.artist_card_number}</span></p>
                )}
                {expense.artist_bank && (
                  <p className="text-xs text-zinc-400">Bank: <span className="text-zinc-50">{expense.artist_bank}</span></p>
                )}
                {expense.artist_ipn && (
                  <p className="text-xs text-zinc-400">IPN: <span className="text-zinc-50 font-mono">{expense.artist_ipn}</span></p>
                )}
              </div>
            )}

            {/* Created/Updated Info */}
            <div className="p-3 bg-zinc-800/30 rounded-lg text-xs space-y-1">
              <p className="text-zinc-500">
                Created: <span className="text-zinc-300">{formatDateTime(expense.created_at)}</span>
                {expense.created_by && <span className="text-zinc-500"> by {expense.created_by}</span>}
              </p>
              {expense.updated_at && (
                <p className="text-zinc-500">
                  Updated: <span className="text-zinc-300">{formatDateTime(expense.updated_at)}</span>
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Change History */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardHeader className="pb-2">
          <CardTitle className="text-zinc-50 flex items-center gap-2 text-base">
            <History className="h-4 w-4 text-cyan-400" />
            Change History ({expense.status_history?.length || 0})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!expense.status_history || expense.status_history.length === 0 ? (
            <div className="text-center py-6 text-zinc-500">
              <History className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">No changes recorded yet</p>
              <p className="text-xs mt-1">Status and field changes will appear here</p>
            </div>
          ) : (
            <div className="space-y-2">
              {expense.status_history.slice().reverse().map((entry, idx) => {
                const isFieldChange = entry.type === "field_change";
                const isStatusChange = entry.status || entry.previous_status;
                
                return (
                  <div
                    key={idx}
                    className={cn(
                      "p-3 rounded-lg border text-sm",
                      isFieldChange
                        ? "bg-cyan-500/5 border-cyan-500/20"
                        : "bg-purple-500/5 border-purple-500/20"
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {isFieldChange ? (
                            <>
                              <Edit3 className="h-3.5 w-3.5 text-cyan-400" />
                              <span className="font-medium text-zinc-300">
                                {entry.field} changed
                              </span>
                              {entry.old_value && (
                                <span className="text-zinc-500 text-xs">from: {entry.old_value}</span>
                              )}
                              <span className="text-zinc-500">→</span>
                              <span className="text-cyan-400">{entry.new_value || "empty"}</span>
                            </>
                          ) : (
                            <>
                              <RefreshCw className="h-3.5 w-3.5 text-purple-400" />
                              <span className="font-medium text-zinc-300">Status changed</span>
                              {entry.previous_status && (
                                <Badge variant="outline" className="text-[10px] h-5 bg-zinc-800 text-zinc-400">
                                  {entry.previous_status}
                                </Badge>
                              )}
                              <span className="text-zinc-500">→</span>
                              <Badge variant="outline" className={cn(
                                "text-[10px] h-5",
                                entry.status === "paid" ? "bg-green-500/10 text-green-400 border-green-500/30" :
                                entry.status === "approved" ? "bg-blue-500/10 text-blue-400 border-blue-500/30" :
                                entry.status === "cancelled" ? "bg-zinc-500/10 text-zinc-400 border-zinc-500/30" :
                                "bg-orange-500/10 text-orange-400 border-orange-500/30"
                              )}>
                                {entry.status}
                              </Badge>
                              {entry.paid_date && (
                                <span className="text-green-400 text-xs">(paid: {entry.paid_date})</span>
                              )}
                            </>
                          )}
                        </div>
                        {entry.reason && (
                          <p className="text-zinc-400 text-xs">{entry.reason}</p>
                        )}
                      </div>
                      <div className="text-right text-[10px] text-zinc-500 flex-shrink-0">
                        <p>{entry.changed_by}</p>
                        <p>{formatDateTime(entry.changed_at)}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Status Change Dialog */}
      <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <RefreshCw className="h-5 w-5 text-purple-400" />
              Change Payment Status
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This action will be recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">New Status</Label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-50">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  <SelectItem value="pending" className="text-orange-400">Pending</SelectItem>
                  <SelectItem value="approved" className="text-blue-400">Approved</SelectItem>
                  <SelectItem value="paid" className="text-green-400">Paid</SelectItem>
                  <SelectItem value="cancelled" className="text-zinc-400">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {newStatus === "paid" && (
              <div className="space-y-2">
                <Label className="text-zinc-400">Payment Date</Label>
                <Input
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  className="bg-zinc-800 border-zinc-700 text-zinc-50"
                />
                <p className="text-[10px] text-green-500">
                  Payment will appear in reports for this month
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-zinc-400">Reason (optional)</Label>
              <Textarea
                value={statusReason}
                onChange={(e) => setStatusReason(e.target.value)}
                placeholder="e.g., Bank transfer completed, Reverting error, etc."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setStatusDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={handleStatusChange}
                disabled={changingStatus}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {changingStatus ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Change Status
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Date Change Dialog */}
      <Dialog open={dateDialogOpen} onOpenChange={setDateDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 text-zinc-50 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-cyan-400" />
              Change {dateField === "paid_date" ? "Payment" : dateField === "approved_date" ? "Approval" : "Creation"} Date
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              This correction will be recorded in the audit log.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-zinc-400">New Date</Label>
              <Input
                type="date"
                value={newDate}
                onChange={(e) => setNewDate(e.target.value)}
                className="bg-zinc-800 border-zinc-700 text-zinc-50"
              />
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-400">Reason (optional)</Label>
              <Textarea
                value={dateReason}
                onChange={(e) => setDateReason(e.target.value)}
                placeholder="e.g., Actual bank transfer date, Accounting correction, etc."
                className="bg-zinc-800 border-zinc-700 text-zinc-50 min-h-[60px]"
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setDateDialogOpen(false)} className="border-zinc-700">
                Cancel
              </Button>
              <Button
                onClick={handleDateChange}
                disabled={changingDate || !newDate}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {changingDate ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Calendar className="h-4 w-4 mr-2" />}
                Update Date
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ArtistPaymentDetailPage;
